import { Component } from '@theme/component';

/** Marker authors type in the first cell to opt a table into heading styles. */
const HEADING_MARKER = /^\s*\[heading\]\s*/i;

/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends Component {
  connectedCallback() {
    super.connectedCallback();

    const mergeFirstRow = this.dataset.mergeFirstRow === 'true';

    this.querySelectorAll('table').forEach((table) => {
      this.#formatTable(table);

      if (!this.#consumeHeadingMarker(table)) return;

      table.classList.add('rte-table--has-heading');
      if (mergeFirstRow) this.#mergeFirstRow(table);
    });
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }

  /**
   * Detects the opt-in marker in the first row and strips it from the rendered text.
   *
   * Edits the text node in place instead of the cell's textContent so inline
   * formatting the editor added (bold, links) survives.
   * @param {HTMLTableElement} table
   * @returns {boolean} whether the table opted in
   */
  #consumeHeadingMarker(table) {
    const firstRow = table.rows[0];
    if (!firstRow) return false;

    for (const cell of firstRow.cells) {
      const walker = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT);

      /** @type {Node | null} */
      let node;
      while ((node = walker.nextNode())) {
        const text = node.nodeValue ?? '';
        if (!text.trim()) continue;

        // Only the first non-empty text node can carry the marker.
        if (!HEADING_MARKER.test(text)) return false;

        node.nodeValue = text.replace(HEADING_MARKER, '');
        return true;
      }
    }

    return false;
  }

  /**
   * Spans the first row's leading cell across every column.
   *
   * The RTE editor can't emit a colspan, and CSS alone can't make a cell span
   * columns, so widen the cell here and drop the now-redundant siblings. Column
   * count is taken from the widest row, since the first row may already be short.
   * @param {HTMLTableElement} table
   */
  #mergeFirstRow(table) {
    const rows = Array.from(table.rows);
    const [firstRow] = rows;
    if (!firstRow) return;

    const columnCount = rows.reduce((max, row) => {
      const span = Array.from(row.cells).reduce((total, cell) => total + (cell.colSpan || 1), 0);
      return Math.max(max, span);
    }, 0);
    if (columnCount < 2) return;

    const [firstCell, ...rest] = Array.from(firstRow.cells);
    if (!firstCell) return;

    // If the editor left the heading text in a trailing cell, move those nodes over
    // rather than assigning textContent, which would flatten the first cell's own
    // markup (and the font-size/color rules that hang off it).
    if (!firstCell.textContent?.trim()) {
      const source = rest.find((cell) => cell.textContent?.trim());
      if (source) firstCell.replaceChildren(...source.childNodes);
    }

    firstCell.colSpan = columnCount;
    rest.forEach((cell) => cell.remove());
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
